<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <b-col
            cols="12"
            md="7"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              variant="outline-primary"
              class="btn-icon"
              :to="{ name: 'events' }"
              ><i class="fa-solid fa-arrow-left"></i
              ><span class="ml-25">Back</span></b-button
            >
            <h3 class="ml-1">Add Event</h3>
          </b-col>
        </b-row>

        <b-form @submit.prevent="onSubmit">
          <b-alert
            v-if="(form.status || '').toLowerCase() === 'draft'"
            variant="warning"
            show
            class="mb-1"
          >
            This event is in draft. Users won't be able to view it until the status is set to "open".
          </b-alert>

          <app-collapse accordion :type="'border'" class="mb-1">
            <!-- Event Details -->
            <app-collapse-item title="Event Details" :isVisible="true">
              <label>Course *</label>
              <div class="d-flex mb-1">
                <v-select
                  class="flex-grow-1"
                  :options="options.courses"
                  label="title"
                  :reduce="(c) => c.id"
                  v-model="form.course_id"
                  @input="onCourseChange"
                  required
                />
                <b-button
                  class="ml-50"
                  variant="outline-primary"
                  @click="toCreateCourse"
                  >New Course</b-button
                >
              </div>

              <b-row>
                <b-col md="4"
                  ><b-form-group label="Year *"
                    ><b-form-input
                      type="number"
                      v-model.number="form.year"
                      min="2000"
                      required/></b-form-group
                ></b-col>
                <b-col md="4"
                  ><b-form-group label="Time zone *"
                    ><b-form-input
                      v-model="form.time_zone"
                      placeholder="Europe/Dublin"
                      required/></b-form-group
                ></b-col>
                <b-col md="4"
                  ><b-form-group label="Status"
                    ><v-select
                      :options="options.enums.event_statuses"
                      v-model="form.status"
                      :reduce="(v) => v"/></b-form-group
                ></b-col>
              </b-row>

              <div
                v-for="(s, idx) in sessions"
                :key="`session-${idx}`"
                class="border rounded p-1 mb-1"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">{{ getPartTitle(idx) }}</h6>
                  <b-button
                    v-if="sessions.length > 1"
                    size="sm"
                    variant="outline-danger"
                    @click="removeSession(idx)"
                    >Remove</b-button
                  >
                </div>
                <b-row class="mt-50">
                  <b-col md="4"
                    ><label>Start</label>
                    <b-form-input type="date" v-model="s.start_date" />
                    <b-form-input
                      class="mt-25"
                      type="text"
                      v-model="s.start_time"
                      placeholder="HH:mm"
                      @blur="onTimeBlur(s, 'start_time')"
                    />
                  </b-col>
                  <b-col md="4"
                    ><label>End</label>
                    <b-form-input type="date" v-model="s.end_date" />
                    <b-form-input
                      class="mt-25"
                      type="text"
                      v-model="s.end_time"
                      placeholder="HH:mm"
                      @blur="onTimeBlur(s, 'end_time')"
                    />
                  </b-col>
                  <b-col md="4">
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Venue</label>
                      <a
                        href="#"
                        @click.prevent="openAddVenueModal(idx)"
                        class="text-primary text-decoration-underline"
                        style="font-size: 0.875rem;"
                        >Add Venue</a
                      >
                    </div>
                    <v-select
                      :options="options.venues"
                      label="name"
                      :reduce="(v) => v.id"
                      v-model="s.venue_id"
                    />
                  </b-col>
                </b-row>
                <b-row class="mt-50">
                  <b-col md="6"
                    ><label>Meeting URL</label
                    ><b-form-input v-model="s.meeting_url"
                  /></b-col>
                  <b-col md="3"
                    ><label>Passcode</label
                    ><b-form-input v-model="s.meeting_passcode"
                  /></b-col>
                  <b-col md="3">
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Trainer</label>
                      <a
                        href="#"
                        @click.prevent="openAddTrainerModal(idx)"
                        class="text-primary text-decoration-underline"
                        style="font-size: 0.875rem;"
                        >Add Trainer</a
                      >
                    </div>
                    <v-select
                      :options="options.trainers"
                      label="name"
                      :reduce="(t) => t.id"
                      v-model="s.primary_trainer_id"
                    />
                  </b-col>
                </b-row>
              </div>
              <b-button size="sm" variant="outline-primary" @click="addSession"
                >+ Add part</b-button
              >

              <b-row class="mt-1">
                <b-col md="3"
                  ><b-form-group label="Max participants"
                    ><b-form-input
                      type="number"
                      min="1"
                      v-model.number="form.max_participants"/></b-form-group
                ></b-col>

                <b-col md="3" class="d-flex align-items-center"
                  ><b-form-checkbox v-model="form.require_manager_approval"
                    >Require manager approval</b-form-checkbox
                  ></b-col
                >

                <b-col md="3" class="d-flex align-items-center"
                  ><b-form-checkbox v-model="form.require_admin_approval"
                    >Require admin approval</b-form-checkbox
                  ></b-col
                >

                <b-col md="3" class="d-flex align-items-center"
                  ><b-form-checkbox v-model="form.is_pre_approved"
                    >Mandatory Training</b-form-checkbox
                  ></b-col
                >
              </b-row>

              <b-form-group label="Title *"
                ><b-form-input v-model="form.title_override" required
              /></b-form-group>
              <b-form-group label="Description"
                ><b-form-textarea v-model="form.description_override" rows="2"
              /></b-form-group>
              <b-card class="mt-1" title="Preview">
                <div>
                  <div class="mb-25">
                    <strong>Course:</strong>
                    {{ (selectedCourse && selectedCourse.title) || '-' }}
                  </div>
                  <div class="mb-25">
                    <strong>Aim:</strong>
                    {{ (selectedCourse && selectedCourse.aim) || '-' }}
                  </div>
                  <div class="mb-25">
                    <strong>Learning outcomes:</strong>
                    <ul class="mb-0">
                      <li
                        v-for="(lo, i) in (selectedCourse &&
                          selectedCourse.learning_outcomes) ||
                          []"
                        :key="i"
                      >
                        {{ lo }}
                      </li>
                    </ul>
                  </div>
                </div>
              </b-card>
            </app-collapse-item>

            <!-- Application Methods -->
            <app-collapse-item title="Application Methods">
              <b-row>
                <b-col md="6"
                  ><b-form-group label="Application method"
                    ><v-select
                      :options="options.enums.application_methods"
                      v-model="form.application_method"
                      :reduce="(v) => v"
                      /></b-form-group
                ></b-col>
                <b-col md="6"
                  ><b-form-group label="Application deadline"
                    ><b-form-input
                      type="date"
                      v-model="form.application_deadline"/></b-form-group
                ></b-col>
              </b-row>
            </app-collapse-item>

            <!-- Materials -->
            <app-collapse-item title="Materials">
              <b-table
                small
                responsive
                bordered
                :items="materials"
                :fields="materialsFields"
                show-empty
                empty-text="No materials"
              >
                <template #cell(title)="{ item }">
                  <div v-if="editingId !== item.id">{{ item.title }}</div>
                  <b-form-input v-else v-model="editRow.title" />
                </template>
                <template #cell(link)="{ item }">
                  <div v-if="item.external_url">
                    <a :href="item.external_url" target="_blank">External</a>
                  </div>
                  <div v-else-if="item.file_path">
                    <a :href="materialFileHref(item.file_path)" target="_blank">Download</a>
                  </div>
                  <div v-else>-</div>
                  <div v-if="editingId === item.id" class="mt-50">
                    <b-form-input
                      v-model="editRow.external_url"
                      placeholder="https://..."
                      class="mb-50"
                    />
                    <b-form-file
                      v-model="editRow.file"
                      browse-text="Choose file"
                      placeholder="No file chosen"
                    />
                  </div>
                </template>
                <template #cell(visible)="{ item }">
                  <div v-if="editingId !== item.id">
                    {{ item.visible_from || '-' }} to {{ item.visible_to || '-' }}
                  </div>
                  <div v-else>
                    <b-form-input type="date" v-model="editRow.visible_from" class="mb-25" />
                    <b-form-input type="date" v-model="editRow.visible_to" :min="editRow.visible_from" />
                  </div>
                </template>
                <template #cell(accepted)="{ item }">
                  <div v-if="editingId !== item.id">
                    <b-badge :variant="item.is_visible_to_accepted_only ? 'primary' : 'light-secondary'">
                      {{ item.is_visible_to_accepted_only ? 'Accepted only' : 'All' }}
                    </b-badge>
                  </div>
                  <div v-else>
                    <b-form-checkbox v-model="editRow.is_visible_to_accepted_only">Accepted only</b-form-checkbox>
                  </div>
                </template>
                <template #cell(course)="{ item }">{{ item.course_id || '-' }}</template>
                <template #cell(actions)="{ item }">
                  <div>
                    <div v-if="editingId !== item.id">
                      <b-button size="sm" variant="outline-primary" class="mr-50" @click="editMaterialStart(item)">Edit</b-button>
                      <b-button size="sm" variant="outline-danger" @click="deleteMaterial(item)">Delete</b-button>
                    </div>
                    <div v-else>
                      <b-button size="sm" variant="primary" class="mr-50" @click="editMaterialSave(item)">Save</b-button>
                      <b-button size="sm" variant="outline-secondary" @click="editMaterialCancel">Cancel</b-button>
                    </div>
                  </div>
                </template>
              </b-table>

              <hr />
              <h6>Add new material</h6>
              <b-form @submit.prevent="uploadMaterial">
                <b-form-group label="Title"
                  ><b-form-input v-model="materialForm.title" required
                /></b-form-group>
                <b-form-group label="External URL (optional)"
                  ><b-form-input
                    v-model="materialForm.external_url"
                    placeholder="https://..."
                /></b-form-group>
                <b-form-group label="File (optional)"
                  ><b-form-file
                    v-model="materialForm.file"
                    browse-text="Choose file"
                    placeholder="No file chosen"
                /></b-form-group>
                <b-row>
                  <b-col md="6"
                    ><b-form-group label="Visible from"
                      ><b-form-input
                        type="date"
                        v-model="materialForm.visible_from"/></b-form-group
                  ></b-col>
                  <b-col md="6"
                    ><b-form-group label="Visible to"
                      ><b-form-input
                        type="date"
                        v-model="materialForm.visible_to"/></b-form-group
                  ></b-col>
                </b-row>
                <b-form-checkbox
                  v-model="materialForm.is_visible_to_accepted_only"
                  >Visible to accepted applicants only</b-form-checkbox
                >
                <div class="mt-1">
                  <b-button type="submit" variant="primary">Upload</b-button>
                </div>
              </b-form>
            </app-collapse-item>

            <!-- Target Audience -->
            <app-collapse-item title="Target Audience">
              <b-row>
                <b-col md="4">
                  <b-form-group>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Companies</label>
                      <div class="text-right">
                        <a
                          v-if="form.company_ids.length === 0"
                          href="#"
                          @click.prevent="selectAllCompanies"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Select All
                        </a>
                        <a
                          v-else
                          href="#"
                          @click.prevent="clearAllCompanies"
                          class="text-danger text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Clear All
                        </a>
                      </div>
                    </div>
                    <v-select
                      multiple
                      :options="options.companies"
                      label="title"
                      :reduce="(c) => c.id"
                      v-model="form.company_ids"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Sites</label>
                      <div class="text-right">
                        <a
                          v-if="form.site_ids.length === 0"
                          href="#"
                          @click.prevent="selectAllSites"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Select All
                        </a>
                        <a
                          v-else
                          href="#"
                          @click.prevent="clearAllSites"
                          class="text-danger text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Clear All
                        </a>
                      </div>
                    </div>
                    <v-select
                      multiple
                      :options="options.sites || []"
                      label="title"
                      :reduce="(s) => s.id"
                      v-model="form.site_ids"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Users</label>
                      <div class="text-right">
                        <a
                          v-if="form.user_ids.length === 0"
                          href="#"
                          @click.prevent="selectAllUsers"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Select All
                        </a>
                        <a
                          v-else
                          href="#"
                          @click.prevent="clearAllUsers"
                          class="text-danger text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Clear All
                        </a>
                      </div>
                    </div>
                    <v-select
                      multiple
                      :options="options.users"
                      label="title"
                      :reduce="(u) => u.id"
                      v-model="form.user_ids"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <label>Roles</label>
                      <div class="text-right">
                        <a
                          v-if="form.role_ids.length === 0"
                          href="#"
                          @click.prevent="selectAllRoles"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Select All
                        </a>
                        <a
                          v-else
                          href="#"
                          @click.prevent="clearAllRoles"
                          class="text-danger text-decoration-underline"
                          style="font-size: 0.875rem;"
                        >
                          Clear All
                        </a>
                      </div>
                    </div>
                    <v-select
                      multiple
                      :options="options.roles"
                      label="name"
                      :reduce="(r) => r.id"
                      v-model="form.role_ids"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-card class="mt-1" title="Event Coordinators">
                <div class="d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">Select coordinators</h6>
                  <div class="text-right">
                    <a
                      v-if="admin_user_ids.length === 0"
                      href="#"
                      @click.prevent="selectAllAdmins"
                      class="text-primary text-decoration-underline"
                      style="font-size: 0.875rem;"
                    >
                      Select All
                    </a>
                    <a
                      v-else
                      href="#"
                      @click.prevent="clearAllAdmins"
                      class="text-danger text-decoration-underline"
                      style="font-size: 0.875rem;"
                    >
                      Clear All
                    </a>
                  </div>
                </div>
                <v-select
                  multiple
                  :options="options.users"
                  label="title"
                  :reduce="(u) => u.id"
                  v-model="admin_user_ids"
                />
              </b-card>
            </app-collapse-item>

            <!-- Event Reminders -->
            <app-collapse-item title="Event Reminders">
              <p class="text-muted small">
                Set up automatic reminders to be sent to participants before the
                event starts.
              </p>
              <div
                v-for="(reminder, idx) in reminders"
                :key="`reminder-${idx}`"
                class="border rounded p-1 mb-1"
              >
                <div
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <h6 class="mb-0">Reminder {{ idx + 1 }}</h6>
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="removeReminder(idx)"
                    >Remove</b-button
                  >
                </div>
                <b-row>
                  <b-col md="4">
                    <label>Type *</label>
                    <v-select
                      :options="reminderTypeOptions"
                      label="label"
                      v-model="reminder.type"
                      :reduce="(opt) => opt.value"
                    />
                  </b-col>
                  <b-col md="4">
                    <label>Days Before Event *</label>
                    <b-form-input
                      type="number"
                      v-model.number="reminder.days_before"
                      min="1"
                      max="30"
                      placeholder="e.g. 3"
                      required
                    />
                  </b-col>
                  <b-col md="8">
                    <label>Custom Message (Optional)</label>
                    <b-form-input
                      v-model="reminder.message"
                      placeholder="Custom reminder message..."
                    />
                  </b-col>
                </b-row>
              </div>
              <b-button size="sm" variant="outline-primary" @click="addReminder"
                >+ Add Reminder</b-button
              >
            </app-collapse-item>
          </app-collapse>

          <div class="mt-2">
            <b-button type="submit" variant="primary">Save</b-button>
            <b-button
              variant="outline-secondary"
              class="ml-1"
              :to="{ name: 'events' }"
              >Cancel</b-button
            >
          </div>
        </b-form>
      </b-overlay>
    </b-card>

    <!-- Add Venue Modal -->
    <b-modal
      v-model="showAddVenueModal"
      title="Add New Venue"
      @ok="handleAddVenue"
      @cancel="resetVenueForm"
      ok-title="Add"
      cancel-title="Cancel"
    >
      <b-form-group label="Name *" label-for="venue-name">
        <b-form-input
          id="venue-name"
          v-model="newVenue.name"
          placeholder="Enter venue name"
          required
        />
      </b-form-group>
    </b-modal>

    <!-- Add Trainer Modal -->
    <b-modal
      v-model="showAddTrainerModal"
      title="Add New Trainer"
      @ok="handleAddTrainer"
      @cancel="resetTrainerForm"
      ok-title="Add"
      cancel-title="Cancel"
    >
      <b-form-group label="Name *" label-for="trainer-name">
        <b-form-input
          id="trainer-name"
          v-model="newTrainer.name"
          placeholder="Enter trainer name"
          required
        />
      </b-form-group>
      <b-form-group label="Email *" label-for="trainer-email">
        <b-form-input
          id="trainer-email"
          v-model="newTrainer.email"
          type="email"
          placeholder="Enter trainer email"
          pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
          required
        />
      </b-form-group>
      <b-form-group label="Phone" label-for="trainer-phone">
        <b-form-input
          id="trainer-phone"
          v-model="newTrainer.phone"
          placeholder="Enter trainer phone"
        />
      </b-form-group>
      <b-form-group label="CV (PDF)" label-for="trainer-cv">
        <b-form-file
          id="trainer-cv"
          v-model="newTrainerCV"
          accept=".pdf"
          placeholder="Choose a PDF file or drop it here..."
          drop-placeholder="Drop PDF file here..."
        />
      </b-form-group>
    </b-modal>
  </div>
</template>
<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BModal,
  BFormFile,
  BAlert,
  BTable,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import eventsStoreModule from "./eventsStoreModule";
import settingsStoreModule from "@/views/settings/settingsStoreModule";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BModal,
    BFormFile,
    BAlert,
    BTable,
    vSelect,
    ToastificationContent,
    AppCollapse,
    AppCollapseItem,
  },
  data() {
    return {
      loading: false,
      options: {
        courses: [],
        users: [],
        trainers: [],
        venues: [],
        companies: [],
        sites: [],
        enums: { event_statuses: [], application_methods: [] },
      },
      form: {
        course_id: null,
        year: new Date().getFullYear(),
        title_override: "",
        description_override: "",
        status: "draft",
        max_participants: null,
        application_method: null,
        application_deadline: null,
        application_description: "",
        require_manager_approval: false,
        require_admin_approval: false,
        is_pre_approved: false,
        time_zone: "Europe/Dublin",
        notes_admin: "",
        company_ids: [],
        site_ids: [],
        user_ids: [],
        role_ids: [],
      },
      sessions: [],
      admin_user_ids: [],
      reminders: [], // Event reminders
      reminderTypeOptions: [
        { label: "Event Reminder", value: "event_reminder" },
        { label: "Application Reminder", value: "application_reminder" },
      ],
      selectedCourse: null,
      applicationDescriptionLabel: "",
      applicationDescriptionPlaceholder: "",
      // Modal states
      showAddVenueModal: false,
      showAddTrainerModal: false,
      currentSessionIndex: null, // Track which session is being edited
      showApplicationDescription: false,
      // New item forms
      newVenue: {
        name: "",
      },
      newTrainer: {
        name: "",
        email: "",
        phone: "",
      },
      newTrainerCV: null,
      // Materials (detached before event is created)
      materials: [],
      materialsFields: [
        { key: "title", label: "Title", thStyle: "min-width: 200px;" },
        { key: "link", label: "Link", thStyle: "min-width: 150px;" },
        { key: "visible", label: "Visible", thStyle: "min-width: 200px;" },
        { key: "accepted", label: "Accepted only", thStyle: "min-width: 120px;" },
        { key: "course", label: "Course", thStyle: "min-width: 150px;" },
        { key: "actions", label: "Actions", thStyle: "min-width: 150px;" },
      ],
      materialForm: {
        title: "",
        file: null,
        external_url: "",
        visible_from: "",
        visible_to: "",
        is_visible_to_accepted_only: true,
        course_id: null,
      },
      editingId: null,
      editRow: {},
    };
  },
  computed: {
    materialsURL() {
      return this.$store.state.app.materialsURL;
    },
  },
  setup() {
    const MODULE = "events";
    if (!store.hasModule(MODULE))
      store.registerModule(MODULE, eventsStoreModule);

    const SETTINGS_MODULE = "settings";
    if (!store.hasModule(SETTINGS_MODULE))
      store.registerModule(SETTINGS_MODULE, settingsStoreModule);

    onUnmounted(() => {
      if (store.hasModule(MODULE)) store.unregisterModule(MODULE);
      if (store.hasModule(SETTINGS_MODULE))
        store.unregisterModule(SETTINGS_MODULE);
    });
  },
  created() {
    this.loadOptions();
    // Ensure there's at least one session to fill by default
    if (this.sessions.length === 0) this.addSession();
  },
  methods: {
    loadOptions() {
      this.loading = true;
      store
        .dispatch("events/fetchEventOptions")
        .then((res) => {
          this.options = res.data;
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Failed to load options",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onCourseChange() {
      const id = this.form.course_id;
      if (!id) {
        this.selectedCourse = null;
        // Reset audience when no course selected
        this.form.company_ids = [];
        this.form.site_ids = [];
        this.form.user_ids = [];
        this.form.role_ids = [];
        return;
      }
      store
        .dispatch("events/getCourseById", { id })
        .then((res) => {
          this.selectedCourse = res.data;
          if (!this.form.max_participants)
            this.form.max_participants = this.selectedCourse.default_max_participants;
          if (!this.form.application_method)
            this.form.application_method = this.selectedCourse.default_application_method;
          if (!this.form.application_deadline)
            this.form.application_deadline = this.selectedCourse.default_application_deadline;
          this.form.require_manager_approval = !!this.selectedCourse
            .require_manager_approval_by_default;

          // Auto-populate title/description from course if empty
          if (!this.form.title_override || this.form.title_override.trim() === "") {
            this.form.title_override = this.selectedCourse.title || "";
          }
          if (
            !this.form.description_override ||
            this.form.description_override.trim() === ""
          ) {
            this.form.description_override = this.selectedCourse.summary || "";
          }

          // Auto-fill audience from course
          this.form.company_ids =
            this.selectedCourse.companies?.map((c) => c.id) || [];
          this.form.site_ids =
            this.selectedCourse.sites?.map((s) => s.id) || [];
          this.form.user_ids =
            (this.selectedCourse.audience_users || [])?.map((u) => u.id) || [];
          this.form.role_ids =
            this.selectedCourse.roles?.map((r) => r.id) || [];
        })
        .catch(() => {});
    },
    addSession() {
      this.sessions.push({
        sequence_no: this.sessions.length + 1,
        start_date: "",
        start_time: "",
        end_date: "",
        end_time: "",
        venue_id: null,
        meeting_url: "",
        meeting_passcode: "",
        primary_trainer_id: null,
      });
    },
    removeSession(i) {
      this.sessions.splice(i, 1);
      this.sessions.forEach((s, idx) => {
        s.sequence_no = idx + 1;
      });
    },
    getPartTitle(idx) {
      const title =
        this.form.title_override ||
        (this.selectedCourse ? this.selectedCourse.title : "");
      // If only one session, don't call it Part 1
      if (this.sessions.length <= 1) {
        return title || "Event timing";
      }
      return title ? `${title} - Part ${idx + 1}` : `Part ${idx + 1}`;
    },
    // Select All / Clear All methods for Companies
    selectAllCompanies() {
      this.form.company_ids = this.options.companies.map((c) => c.id);
    },
    clearAllCompanies() {
      this.form.company_ids = [];
    },
    // Select All / Clear All methods for Users
    selectAllUsers() {
      this.form.user_ids = this.options.users.map((u) => u.id);
    },
    clearAllUsers() {
      this.form.user_ids = [];
    },
    // Select All / Clear All methods for Sites
    selectAllSites() {
      const sites = this.options.sites || [];
      this.form.site_ids = sites.map((s) => s.id);
    },
    clearAllSites() {
      this.form.site_ids = [];
    },
    // Select All / Clear All methods for Roles
    selectAllRoles() {
      this.form.role_ids = this.options.roles.map((r) => r.id);
    },
    clearAllRoles() {
      this.form.role_ids = [];
    },
    // Select All / Clear All methods for Admins
    selectAllAdmins() {
      this.admin_user_ids = this.options.users.map((u) => u.id);
    },
    clearAllAdmins() {
      this.admin_user_ids = [];
    },

    // Modal handlers
    openAddVenueModal(sessionIndex) {
      this.currentSessionIndex = sessionIndex;
      this.showAddVenueModal = true;
    },
    openAddTrainerModal(sessionIndex) {
      this.currentSessionIndex = sessionIndex;
      this.showAddTrainerModal = true;
    },
    async handleAddVenue(bvModalEvent) {
      bvModalEvent.preventDefault();
      if (!this.newVenue.name.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please enter venue name",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      try {
        // Add to options via API call
        const response = await store.dispatch("settings/createVenue", {
          name: this.newVenue.name.trim(),
        });
        const newVenue = response.data.data;
        this.options.venues.push(newVenue);

        // Auto-select the newly added venue in the current session
        if (
          this.currentSessionIndex !== null &&
          this.sessions[this.currentSessionIndex]
        ) {
          this.sessions[this.currentSessionIndex].venue_id = newVenue.id;
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Venue added successfully",
            icon: "CheckIcon",
            variant: "success",
          },
        });

        this.resetVenueForm();
        this.showAddVenueModal = false;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error adding venue",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    async handleAddTrainer(bvModalEvent) {
      bvModalEvent.preventDefault();
      if (!this.newTrainer.name.trim() || !this.newTrainer.email.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please fill all required trainer fields",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      // Email format validation
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
      if (!emailRegex.test(this.newTrainer.email.trim())) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please enter a valid email address",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append("name", this.newTrainer.name.trim());
        formData.append("email", this.newTrainer.email.trim());
        if (this.newTrainer.phone.trim()) {
          formData.append("phone", this.newTrainer.phone.trim());
        }
        if (this.newTrainerCV) {
          formData.append("cv_pdf", this.newTrainerCV);
        }

        // Add to options via API call
        const response = await store.dispatch(
          "settings/createTrainerWithFile",
          formData
        );
        const newTrainer = response.data.data;
        this.options.trainers.push(newTrainer);

        // Auto-select the newly added trainer in the current session
        if (
          this.currentSessionIndex !== null &&
          this.sessions[this.currentSessionIndex]
        ) {
          this.sessions[this.currentSessionIndex].primary_trainer_id =
            newTrainer.id;
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Trainer added successfully",
            icon: "CheckIcon",
            variant: "success",
          },
        });

        this.resetTrainerForm();
        this.showAddTrainerModal = false;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error adding trainer",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    resetVenueForm() {
      this.newVenue = {
        name: "",
      };
    },
    resetTrainerForm() {
      this.newTrainer = {
        name: "",
        email: "",
        phone: "",
      };
      this.newTrainerCV = null;
    },
    toCreateCourse() {
      this.$router.push({ name: "settings-course-add" });
    },
    async onSubmit() {
      try {
        // Validate required fields
        if (!this.form.course_id) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please select a course",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        if (!this.form.year) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please enter a year",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        if (!this.form.time_zone) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please enter a time zone",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        // Validate application description if needed
        // if (
        //   this.form.application_method &&
        //   this.form.application_method.toLowerCase() !== "calendar"
        // ) {
        //   if (!this.form.application_description) {
        //     this.$toast({
        //       component: ToastificationContent,
        //       props: {
        //         title: "Validation Error",
        //         text: "Please provide application details",
        //         icon: "AlertTriangleIcon",
        //         variant: "danger",
        //       },
        //     });
        //     return;
        //   }
        //   if (
        //     this.form.application_method.toLowerCase() === "email" &&
        //     !/^([^\s@]+)@([^\s@]+)\.[^\s@]+$/.test(
        //       this.form.application_description.trim()
        //     )
        //   ) {
        //     this.$toast({
        //       component: ToastificationContent,
        //       props: {
        //         title: "Validation Error",
        //         text: "Please enter a valid application email address",
        //         icon: "AlertTriangleIcon",
        //         variant: "danger",
        //       },
        //     });
        //     return;
        //   }
        //   if (
        //     this.form.application_method.toLowerCase() === "external" &&
        //     !/^(https?:\/\/)?([\w.-]+)\.([a-z\.]{2,6})([\/\w .-]*)*\/?$/i.test(
        //       this.form.application_description.trim()
        //     )
        //   ) {
        //     this.$toast({
        //       component: ToastificationContent,
        //       props: {
        //         title: "Validation Error",
        //         text: "Please enter a valid application website URL",
        //         icon: "AlertTriangleIcon",
        //         variant: "danger",
        //       },
        //     });
        //     return;
        //   }
        // }

        if (
          !this.form.title_override ||
          this.form.title_override.trim() === ""
        ) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please enter a title",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        // Warn if status is draft before saving
        if ((this.form.status || '').toLowerCase() === 'draft') {
          const result = await this.$swal({
            title: 'Event is in draft',
            text:
              "Users won't be able to view this event until the status is set to 'open'. Do you want to proceed?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Proceed',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
          });
          if (!result.isConfirmed) {
            return;
          }
        }

        // Validate all sessions before creating event
        for (const s of this.sessions) {
          // Skip sessions with empty required fields
          if (!s.start_date || !s.start_time || !s.end_date || !s.end_time) {
            continue;
          }

          const st = this._normalizeTime(s.start_time);
          const et = this._normalizeTime(s.end_time);
          const start = this._toDateObj(`${s.start_date}T${st}:00`);
          const end = this._toDateObj(`${s.end_date}T${et}:00`);
          if (!st || !et || !start || !end || end <= start) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Session Date Error",
                text: `Part ${s.sequence_no}: End time must be after start time`,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
            return; // Stop here, don't create anything
          }
        }

        this.loading = true;
        // create event
        const ev = await store.dispatch("events/saveEvent", {
          ...this.form,
          material_ids: (this.materials || []).map((m) => m.id),
          reminders: this.reminders,
        });
        const eventId = ev.data.id;
        // assign admins if chosen
        if (this.admin_user_ids.length) {
          await store.dispatch("events/setEventAdmins", {
            id: eventId,
            admin_user_ids: this.admin_user_ids,
          });
        }
        // create sessions
        for (const s of this.sessions) {
          // Skip sessions with empty required fields (date/time pairs)
          if (!s.start_date || !s.start_time || !s.end_date || !s.end_time) {
            console.log("Skipping session with incomplete dates:", s);
            continue;
          }

          // Validation is already performed above; now format datetime for backend
          const sessionData = {
            sequence_no: s.sequence_no,
            venue_id: s.venue_id,
            meeting_url: s.meeting_url,
            meeting_passcode: s.meeting_passcode,
            primary_trainer_id: s.primary_trainer_id,
            start_at: this._combineDateTimeForBackend(s.start_date, s.start_time),
            end_at: this._combineDateTimeForBackend(s.end_date, s.end_time),
          };
          await store.dispatch("events/addSession", {
            id: eventId,
            data: sessionData,
          });
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Event created",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.$router.push({ name: "events" });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Save failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    // Reminder Methods
    addReminder() {
      this.reminders.push({
        days_before: 3,
        message: "",
        type: "event_reminder",
        is_active: true,
      });
    },
    removeReminder(index) {
      this.reminders.splice(index, 1);
    },
    // Helpers for datetime parsing/formatting
    _toDateObj(val) {
      if (!val || typeof val !== 'string') return null;
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    },
    _normalizeTime(time) {
      if (!time || typeof time !== 'string') return null;
      const t = time.trim();
      if (/^\d{1,2}:\d{2}$/.test(t)) return this._padTime(t);
      if (/^\d{3,4}$/.test(t)) {
        const part = t.length === 3 ? `0${t}` : t;
        const hh = part.slice(0, 2);
        const mm = part.slice(2);
        return this._padTime(`${hh}:${mm}`);
      }
      return null;
    },
    _padTime(tt) {
      const [h, m] = tt.split(':');
      const hh = String(parseInt(h, 10)).padStart(2, '0');
      const mm = String(parseInt(m, 10)).padStart(2, '0');
      if (+hh < 0 || +hh > 23 || +mm < 0 || +mm > 59) return null;
      return `${hh}:${mm}`;
    },
    _combineDateTimeForBackend(date, time) {
      const t = this._normalizeTime(time);
      if (!date || !t) return null;
      return `${date} ${t}:00`;
    },
    onTimeBlur(session, key) {
      const norm = this._normalizeTime(session[key]);
      if (norm) session[key] = norm;
    },
    // Materials helpers & actions (detached)
    materialFileHref(path) {
      const base = this.materialsURL || '';
      return base.replace(/\/$/, '') + '/' + String(path || '').replace(/^\//, '');
    },
    async uploadMaterial() {
      try {
        this.loading = true;
        // Validate visible date order
        if (this.materialForm.visible_from && this.materialForm.visible_to) {
          const from = new Date(this.materialForm.visible_from);
          const to = new Date(this.materialForm.visible_to);
          if (to < from) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Material Date Error',
                text: 'Visible to date must be on or after Visible from date',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            });
            this.loading = false;
            return;
          }
        }
        const fd = new FormData();
        fd.append("title", this.materialForm.title || "");
        if (this.materialForm.file) fd.append("file", this.materialForm.file);
        if (this.materialForm.external_url)
          fd.append("external_url", this.materialForm.external_url);
        if (this.materialForm.visible_from)
          fd.append("visible_from", this.materialForm.visible_from);
        if (this.materialForm.visible_to)
          fd.append("visible_to", this.materialForm.visible_to);
        fd.append(
          "is_visible_to_accepted_only",
          this.materialForm.is_visible_to_accepted_only ? 1 : 0
        );
        if (this.materialForm.course_id)
          fd.append("course_id", this.materialForm.course_id);

        const res = await store.dispatch("events/uploadDetachedMaterial", fd);
        const newId = (res.data && res.data.id) || null;
        // Push locally so user can edit before event creation
        this.materials.push({
          id: newId,
          title: this.materialForm.title,
          external_url: this.materialForm.external_url,
          file_path: null, // not returned by API; will appear after edit page
          visible_from: this.materialForm.visible_from,
          visible_to: this.materialForm.visible_to,
          is_visible_to_accepted_only: this.materialForm.is_visible_to_accepted_only,
          course_id: this.materialForm.course_id || null,
        });

        this.$toast({
          component: ToastificationContent,
          props: { title: "Material uploaded", icon: "ThumbsUpIcon", variant: "success" },
        });
        // reset form
        this.materialForm = {
          title: "",
          file: null,
          external_url: "",
          visible_from: "",
          visible_to: "",
          is_visible_to_accepted_only: true,
          course_id: null,
        };
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: "Upload failed", icon: "AlertTriangleIcon", variant: "danger" },
        });
      } finally {
        this.loading = false;
      }
    },
    editMaterialStart(m) {
      this.editingId = m.id;
      this.editRow = {
        title: m.title,
        external_url: m.external_url,
        visible_from: m.visible_from,
        visible_to: m.visible_to,
        is_visible_to_accepted_only: !!m.is_visible_to_accepted_only,
        course_id: m.course_id || null,
        file: null,
      };
    },
    editMaterialCancel() {
      this.editingId = null;
      this.editRow = {
        title: "",
        external_url: "",
        visible_from: "",
        visible_to: "",
        is_visible_to_accepted_only: true,
        course_id: null,
        file: null,
      };
    },
    async editMaterialSave(m) {
      try {
        this.loading = true;
        if (this.editRow.visible_from && this.editRow.visible_to) {
          const from = new Date(this.editRow.visible_from);
          const to = new Date(this.editRow.visible_to);
          if (to < from) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Material Date Error',
                text: 'Visible to date must be on or after Visible from date',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            });
            this.loading = false;
            return;
          }
        }
        const fd = new FormData();
        if (this.editRow.title) fd.append("title", this.editRow.title);
        if (this.editRow.external_url) fd.append("external_url", this.editRow.external_url);
        if (this.editRow.visible_from) fd.append("visible_from", this.editRow.visible_from);
        if (this.editRow.visible_to) fd.append("visible_to", this.editRow.visible_to);
        if (typeof this.editRow.is_visible_to_accepted_only !== "undefined") {
          fd.append(
            "is_visible_to_accepted_only",
            this.editRow.is_visible_to_accepted_only ? 1 : 0
          );
        }
        if (this.editRow.course_id) fd.append("course_id", this.editRow.course_id);
        if (this.editRow.file) fd.append("file", this.editRow.file);

        await store.dispatch("events/updateMaterial", { materialId: m.id, data: fd });
        // Update local row
        const idx = this.materials.findIndex((x) => x.id === m.id);
        if (idx !== -1) {
          this.$set(this.materials, idx, { ...this.materials[idx], ...this.editRow });
        }
        this.$toast({
          component: ToastificationContent,
          props: { title: "Material updated", icon: "ThumbsUpIcon", variant: "success" },
        });
        this.editMaterialCancel();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: "Update failed", icon: "AlertTriangleIcon", variant: "danger" },
        });
      } finally {
        this.loading = false;
      }
    },
    async deleteMaterial(m) {
      const ok = await this.$bvModal.msgBoxConfirm("Delete this material?", {
        size: "sm",
        title: "Confirm",
        okVariant: "primary",
        okTitle: "Yes",
        cancelTitle: "No",
        cancelVariant: "outline-secondary",
        centered: true,
      });
      if (!ok) return;
      try {
        this.loading = true;
        await store.dispatch("events/deleteMaterial", { materialId: m.id });
        this.materials = this.materials.filter((x) => x.id !== m.id);
        this.$toast({
          component: ToastificationContent,
          props: { title: "Material deleted", icon: "ThumbsUpIcon", variant: "success" },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: "Delete failed", icon: "AlertTriangleIcon", variant: "danger" },
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
